import { useCallback, useEffect, useState, useRef, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useGetPetInfoQuery, useUpdateReimbursementInformationMutation } from '../../../data/policy';
import { useUpdateAllReimbursementInformationMutation } from '../../../data/customer';
import { PAYMENT_METHOD, PETSURE_UNDERWRITER, PAYMENT_METHOD_OPTION } from '../../../constants/constants';
import { FORM_KEYS } from '../../../constants/formKeys';
import { configureHttpClient } from '../../../utils/payment/httpClientConfig';
import { showSelectAllPetCheckbox, legacyPolicyNumberHandler } from '../../../helpers/paymentHelper';
import { setReimbursement } from '../../../store/slice/claimSlice';
import { SLICES } from '../../../constants/slices';
import { useRegisterAccountMutation } from '../../../data/premiumsandpayments';
import { BrandContext } from '../../../contextProviders/brandContext';

export const useReimbursementPopup = ({ activePet, onCancel, setShowSuccessMessage, isLegacyPaymentEnabled }) => {
  const fieldFormContext = useFormContext();

  const dispatch = useDispatch();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const [policyDetails, setPolicyDetails] = useState();
  const [showFailedUpdateError, setShowFailedUpdateError] = useState(false);
  const [successfulUpdate, setSuccessfulUpdate] = useState();
  const [paymentMethod, setPaymentMethod] = useState(0);
  const ddRef = useRef();
  const applyToAll = fieldFormContext.watch(FORM_KEYS.APPLY_TO_ALL_PETS);
  const { ACCOUNT_NAME, BSB, ACCOUNT_NUMBER } = FORM_KEYS;

  const resetReimbursementFields = useCallback(() => {
    fieldFormContext.resetField(ACCOUNT_NAME);
    fieldFormContext.resetField(BSB);
    fieldFormContext.resetField(ACCOUNT_NUMBER);
    fieldFormContext.resetField(FORM_KEYS.APPLY_TO_ALL_PETS);
  }, [fieldFormContext, ACCOUNT_NAME, BSB, ACCOUNT_NUMBER]);

  const { data: petDetails } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const [updateReimbursementInformation] = useUpdateReimbursementInformationMutation();
  const [updateAllReimbursementInformation] = useUpdateAllReimbursementInformationMutation();
  const [registerAccount] = useRegisterAccountMutation();

  const {
    activeBrandContext: {
      brand: { partner },
      cspConfiguration: {
        creditCardConfig: {
          [process.env.REACT_APP_ENV]: { supplierBusinessCode }
        }
      }
    }
  } = useContext(BrandContext);

  const isPartnerUsingLegacyPayment = partner === 'trupanion';

  // Checks if all pets have the same underwriter
  const showSelectAllCheckbox = showSelectAllPetCheckbox(petDetails);

  // configure http client
  useEffect(() => {
    configureHttpClient(authDetails);
  }, [authDetails]);

  const dispatchReimbursementUpdate = () => {
    if (isLegacyPaymentEnabled) return;

    const { accountName, accountNumber } = fieldFormContext.getValues();

    dispatch(
      setReimbursement({
        hasNewReimbursement: true,
        newReimbursementDetails: {
          newAccountHolderName: accountName,
          newAccountNumber: accountNumber
        }
      })
    );
  };

  const handleUpdateDirectDebit = async () => {
    if (activePet.Underwriter === PETSURE_UNDERWRITER || isPartnerUsingLegacyPayment) {
      const isValid = await fieldFormContext.trigger([ACCOUNT_NAME, BSB, ACCOUNT_NUMBER], {
        shouldFocus: true
      });

      if (isValid) {
        const { accountName, bsb, accountNumber, applyToAll } = fieldFormContext.getValues();
        // Remove any dashes from the 'bsb' value
        const bsbNumber = bsb.replace(/-/g, '');
        const isPetsureUnderwriter = activePet.Underwriter === PETSURE_UNDERWRITER;
        const directDebitPayload = {
          PolicyNumber: activePet.PolicyNumber,
          PartnerNumber: activePet.PartnerNumber,
          PaymentMethod: PAYMENT_METHOD.BANK,
          MethodOfReimbursement: 0,
          BsbNumber: bsbNumber,
          Bank: '',
          AccountNumber: accountNumber,
          AccountHoldeName: accountName,
          Token: 'D0'
        };

        try {
          if (applyToAll) {
            const response = await updateAllReimbursementInformation({
              data: directDebitPayload,
              isPetsureUnderwriter: isPartnerUsingLegacyPayment ? true : isPetsureUnderwriter
            }).unwrap();

            if (response?.isSuccessResponse === false) {
              setShowFailedUpdateError(true);
              return;
            }
          } else {
            const response = await updateReimbursementInformation({
              data: directDebitPayload,
              policyNumber: legacyPolicyNumberHandler(activePet.PolicyNumber),
              isPetsureUnderwriter: isPartnerUsingLegacyPayment ? true : isPetsureUnderwriter
            }).unwrap();

            if (response?.isSuccessResponse === false) {
              setShowFailedUpdateError(true);
              return;
            }
          }

          dispatchReimbursementUpdate();
          setSuccessfulUpdate({ isApplyToAllPetChecked: applyToAll });
        } catch {
          setShowFailedUpdateError(true);
        }
      }
    } else if (paymentMethod === PAYMENT_METHOD_OPTION.CARD) {
      ddRef.current?.submitDirectDebit();
    } else {
      const isPetsureUnderwriter = activePet.Underwriter === PETSURE_UNDERWRITER;
      const chequePayload = {
        AccountHoldeName: '',
        AccountNumber: '',
        Bank: '',
        BsbNumber: '',
        PartnerNumber: activePet.PartnerNumber,
        PaymentMethod: PAYMENT_METHOD.CHEQUE,
        PolicyNumber: activePet.PolicyNumber,
        Token: ''
      };

      try {
        if (applyToAll) {
          const response = await updateAllReimbursementInformation({
            data: chequePayload,
            isPetsureUnderwriter
          }).unwrap();

          if (response?.isSuccessResponse === false) {
            setShowFailedUpdateError(true);
            return;
          }
        } else {
          const response = await updateReimbursementInformation({
            data: chequePayload,
            policyNumber: legacyPolicyNumberHandler(activePet.PolicyNumber),
            isPetsureUnderwriter
          }).unwrap();

          if (response?.isSuccessResponse === false) {
            setShowFailedUpdateError(true);
            return;
          }
        }

        dispatchReimbursementUpdate();
        setSuccessfulUpdate({ isApplyToAllPetChecked: applyToAll });
      } catch {
        setShowFailedUpdateError(true);
      }
    }
  };

  useEffect(() => {
    if (!successfulUpdate) return;

    onCancel();
    resetReimbursementFields();
    setShowSuccessMessage({
      active: true,
      isApplyToAllPetChecked: successfulUpdate.isApplyToAllPetChecked
    });
  }, [successfulUpdate, onCancel, setShowSuccessMessage, resetReimbursementFields]);

  useEffect(() => {
    if (activePet && petDetails) {
      setPolicyDetails({
        policyNumber: activePet.PolicyNumber,
        partnerNumber: activePet.PartnerNumber,
        selectAllPetEnabled: showSelectAllCheckbox && petDetails.length > 1
      });
    }
  }, [activePet, petDetails, showSelectAllCheckbox]);

  const handleClose = () => {
    resetReimbursementFields();
    onCancel();
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
  };

  const handlePaymentSuccess = async (result) => {
    try {
      const response = await registerAccount({
        singleUseToken: result.singleUseTokenId
      }).unwrap();

      const directDebitResp = response?.responseObject?.RegisterDirectDebitAccountResp;

      const { AccountToken, AccountName, Bsb, AccountNumber } = directDebitResp;

      const directDebitPayload = {
        AccountHoldeName: AccountName,
        AccountNumber,
        Bank: '',
        BsbNumber: Bsb.replace(/-/g, ''),
        PartnerNumber: activePet.PartnerNumber,
        PaymentMethod: PAYMENT_METHOD.BANK,
        Token: AccountToken
      };

      const isPetsureUnderwriter = activePet.Underwriter === PETSURE_UNDERWRITER;

      if (applyToAll) {
        const responses = await Promise.all(
          petDetails.map((pet) =>
            updateAllReimbursementInformation({
              data: {
                ...directDebitPayload,
                PolicyNumber: pet.PolicyNumber
              },
              policyNumber: pet.PolicyNumber,
              isPetsureUnderwriter
            })
              .unwrap()
              .catch(() => ({ isSuccessResponse: false }))
          )
        );

        if (responses.some((reimbursementResponse) => reimbursementResponse?.isSuccessResponse === false)) {
          setShowFailedUpdateError(true);
          return;
        }
      } else {
        const reimbursementResponse = await updateReimbursementInformation({
          data: {
            ...directDebitPayload,
            PolicyNumber: activePet.PolicyNumber
          },
          policyNumber: legacyPolicyNumberHandler(activePet.PolicyNumber),
          isPetsureUnderwriter
        }).unwrap();

        if (reimbursementResponse?.isSuccessResponse === false) {
          setShowFailedUpdateError(true);
          return;
        }
      }

      dispatchReimbursementUpdate();
      setSuccessfulUpdate({ isApplyToAllPetChecked: applyToAll });
    } catch {
      setShowFailedUpdateError(true);
    }
  };

  // Legacy payment handlers

  return {
    fieldFormContext,
    showSelectAllCheckbox,
    policyDetails,
    showFailedUpdateError,
    petDetails,
    paymentMethod,
    supplierBusinessCode,
    ddRef,
    handleUpdateDirectDebit,
    handleClose,
    handlePaymentMethodChange,
    handlePaymentSuccess,
    isPartnerUsingLegacyPayment
  };
};
